import { useNotificationContext } from "@4uhub/lib4uhub";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import {
  answerEvaluationFormQuestion,
  updateAnswerEvaluationFormQuestion,
} from "../../../services/evaluationForm.service";
import { useEvaluationFormContext } from "../../../store/contexts/EvaluationFormContext";
import { useFormStepperContext } from "../../../store/contexts/FormStepperContext";
import { FormStepsActions } from "../../UI/FormsStepper/FormSteps/FormStepsActions/FormStepsActions";
import RadioGroupInput from "../../UI/Inputs/RadioGroupInput";
import { JustificationField } from "./JustificationField/JustificationField";
import { IStepProps } from "./models";
import { TStepForm, stepSchema } from "./stepSchema";
import FormInput from "../../UI/Inputs/FormInput";
import { alternativeRequiresJustification } from "./justificationRules";
import { QuestionGuidanceText } from "../QuestionGuidanceText";

const translationPath = "components.step.";
const LIKERT_CODES = ["7", "8", "9", "10"];
const GUIDANCE_TEXT_POSITION_BEFORE = "1";
const GUIDANCE_TEXT_POSITION_AFTER = "2";

const getAlternativeLabel = (alt: {
  label: string | null;
  nrOption: number;
}): string =>
  alt.label !== null && alt.label !== undefined
    ? alt.label
    : alt.nrOption.toString();

export const Step = memo(
  ({ stepsLength, question, hasAnswerDate }: IStepProps) => {
    const { t } = useTranslation();

    const { handleNext } = useFormStepperContext();

    const { id, type } = useParams();

    const { setMessage } = useNotificationContext();

    const { answers, addAnswer, updateAnswer } = useEvaluationFormContext();

    const currentAnswer = useMemo(
      () => answers.find((asw) => asw.questionId === question.id),
      [answers, question.id],
    );
    const code = String(question?.questionEvaluationType?.code ?? "");
    const methods = useForm<TStepForm>({
      resolver: zodResolver(stepSchema),
      defaultValues: {
        questionAlternative: currentAnswer?.questionAlternativeId ?? "",
        justification: currentAnswer?.justification ?? "",
        answer: currentAnswer?.answer ?? "",
      },
    });

    const { handleSubmit, setError, reset } = methods;

    const { sendRequest, loading } = useFetch(answerEvaluationFormQuestion);

    const { sendRequest: update } = useFetch(
      updateAnswerEvaluationFormQuestion,
    );

    const onSubmitHandler = useCallback(
      async (form: TStepForm) => {
        const currentAlternative = question.questionAlternatives.find(
          (alt) => alt.id === form.questionAlternative,
        );

        const isSimpleChoice = code === "11";
        const selectedOptionRequiresJustification =
          !!currentAlternative &&
          question.questionOptionType?.isRequiredJustification &&
          alternativeRequiresJustification(
            currentAlternative.integrationId,
            currentAlternative.nrOption,
            isSimpleChoice
              ? question.questionOptionType.requiredJustificationOptions
              : null,
            isSimpleChoice
              ? null
              : question.questionOptionType.requiredJustificationLessThan,
          );

        if (
          (selectedOptionRequiresJustification && !form.justification) ||
          (form.justification &&
            form.justification.length <
              question.questionOptionType.requiredJustificationMinChars)
        ) {
          setError("justification", {
            type: "min",
            message: question.questionOptionType.requiredJustificationMessage,
          });
          return;
        }

        if (
          question?.questionEvaluationType?.code &&
          (["1", "2", "7", "8", "9", "10"].includes(code) ||
            (isSimpleChoice && question.questionOptionType?.isRequired))
        ) {
          if (!form.questionAlternative) {
            setError("questionAlternative", {
              type: "min",
              message: `${t(translationPath + "errors.alternative")}`,
            });
            return;
          }
        }

        if (
          question?.questionEvaluationType?.code &&
          ["3", "4", "5"].includes(
            String(question.questionEvaluationType.code),
          ) &&
          question.questionOptionType?.isRequired
        ) {
          if (
            !form.answer ||
            form.answer.length < question.questionOptionType.minChars
          ) {
            setError("answer", {
              type: "min",
              message: `${t(translationPath + "errors.answer", {
                min: question.questionOptionType.minChars,
              })}`,
            });
            return;
          }
        }
        let derivedAnswer: string | undefined = form.answer ?? undefined;
        const derivedQuestionAlternativeId: string | undefined =
          form.questionAlternative || undefined;

        const chosenAlternative = question.questionAlternatives.find(
          (alt) => alt.id === form.questionAlternative,
        );

        if (chosenAlternative) {
          derivedAnswer =
            chosenAlternative.integrationId ??
            String(chosenAlternative.nrOption);
        }
        if (!id) return;

        if (!currentAnswer) {
          const { data, success } = await sendRequest({
            evalMovEncSatisfactionId: type ? undefined : id,
            evalMovTicketId: type ? id : undefined,
            questionAlternativeId: derivedQuestionAlternativeId,
            answer: derivedAnswer,
            justification: form.justification ?? undefined,
            questionId: question.id,
          });
          if (data && success) {
            addAnswer({
              id: data.id,
              questionId: question.id,
              justification: form.justification ?? "",
              questionAlternativeId: derivedQuestionAlternativeId,
              answer: derivedAnswer,
            });
            setMessage({
              message: `${t(translationPath + "answer_success")}`,
              type: "success",
            });
            handleNext();
          }
        } else {
          if (
            currentAnswer.questionAlternativeId !== form.questionAlternative ||
            currentAnswer.justification !== form.justification ||
            currentAnswer.answer !== derivedAnswer
          ) {
            const { success } = await update({
              id: currentAnswer.id,
              questionAlternativeId: derivedQuestionAlternativeId,
              justification: form.justification ?? "",
              evalMovTicketId: type ? id : undefined,
              answer: derivedAnswer,
              questionId: question.id,
            });
            if (success) {
              updateAnswer({
                id: question.id,
                questionId: question.id,
                justification: form.justification ?? "",
                questionAlternativeId: derivedQuestionAlternativeId,
                answer: derivedAnswer,
              });
              setMessage({
                message: `${t(translationPath + "update_answer_success")}`,
                type: "success",
              });
            }
          }
          handleNext();
        }
      },
      [
        question,
        id,
        type,
        currentAnswer,
        code,
        setError,
        handleNext,
        sendRequest,
        update,
        addAnswer,
        updateAnswer,
        setMessage,
        t,
      ],
    );

    const getAnswer = useCallback(() => {
      if (currentAnswer) {
        reset({
          questionAlternative: currentAnswer.questionAlternativeId,
          justification: currentAnswer.justification,
          answer: currentAnswer.answer,
        });
      }
    }, [currentAnswer, reset]);

    useEffect(() => {
      getAnswer();
    }, [getAnswer]);

    return (
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmitHandler)}
          sx={{
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {question.guidanceText &&
              question.guidanceTextPosition?.code ===
                GUIDANCE_TEXT_POSITION_BEFORE && (
                <QuestionGuidanceText
                  guidanceText={question.guidanceText}
                  maxWidth={code === "8" ? 1000 : 600}
                />
              )}
            <Typography fontSize={18} fontWeight="bold">
              {question.title}
            </Typography>
            {question.description && question.description.length > 0 && (
              <Typography
                variant="subtitle2"
                sx={(t) => ({
                  color: t.palette.grey[t.palette.mode === "light" ? 600 : 300],
                })}
              >
                {question.description}
              </Typography>
            )}
            <Box
              sx={{
                mt: 2,
                width: "100%",
                maxWidth: code === "8" ? 1000 : 600,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {question?.questionEvaluationType?.code &&
              ["3", "4", "5"].includes(code) ? (
                <FormInput
                  name="answer"
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                  disabled={hasAnswerDate}
                />
              ) : code === "6" ? (
                <RadioGroupInput
                  name="answer"
                  groupLabel=""
                  disableBorder
                  inline
                  fullwidth
                  disabled={hasAnswerDate}
                  radios={[
                    { label: "Sim", value: "01" },
                    { label: "Não", value: "00" },
                  ]}
                />
              ) : LIKERT_CODES.includes(code) ? (
                <>
                  <RadioGroupInput
                    name="questionAlternative"
                    groupLabel=""
                    disableBorder
                    inline
                    fullwidth
                    disabled={hasAnswerDate}
                    radios={[...question.questionAlternatives]
                      .sort((a, b) => a.nrOption - b.nrOption)
                      .map((alt) => ({
                        label: getAlternativeLabel(alt),
                        value: alt.id,
                      }))}
                  />
                  {question.questionOptionType?.isRequiredJustification && (
                    <JustificationField
                      requiredJustificationLessThan={
                        question.questionOptionType
                          .requiredJustificationLessThan
                      }
                      questionAlternatives={question.questionAlternatives}
                      hasAnswerDate={hasAnswerDate}
                      alwaysShowJustification={
                        question.questionOptionType.alwaysShowJustification
                      }
                      isRequiredJustification={
                        question.questionOptionType.isRequiredJustification
                      }
                      requiredJustificationMessage={
                        question.questionOptionType.requiredJustificationMessage
                      }
                    />
                  )}
                </>
              ) : (
                <>
                  <RadioGroupInput
                    name="questionAlternative"
                    groupLabel=""
                    disableBorder
                    inline
                    fullwidth
                    disabled={hasAnswerDate}
                    radios={[...question.questionAlternatives]
                      .sort((a, b) => a.nrOption - b.nrOption)
                      .map((alt) => ({
                        label: getAlternativeLabel(alt),
                        value: alt.id,
                      }))}
                  />
                  {(["1", "2"].includes(code) ||
                    (code === "11" &&
                      (question.questionOptionType?.isRequiredJustification ||
                        question.questionOptionType
                          ?.alwaysShowJustification))) && (
                    <JustificationField
                      requiredJustificationLessThan={
                        question.questionOptionType
                          .requiredJustificationLessThan
                      }
                      requiredJustificationOptions={
                        question.questionOptionType.requiredJustificationOptions
                      }
                      questionAlternatives={question.questionAlternatives}
                      hasAnswerDate={hasAnswerDate}
                      alwaysShowJustification={
                        question.questionOptionType.alwaysShowJustification
                      }
                      isRequiredJustification={
                        question.questionOptionType.isRequiredJustification
                      }
                      requiredJustificationMessage={
                        question.questionOptionType.requiredJustificationMessage
                      }
                    />
                  )}
                </>
              )}
              {question.guidanceText &&
                question.guidanceTextPosition?.code ===
                  GUIDANCE_TEXT_POSITION_AFTER && (
                  <QuestionGuidanceText guidanceText={question.guidanceText} />
                )}
            </Box>
          </Box>
          <FormStepsActions
            stepsLength={stepsLength}
            type="submit"
            stepLoading={loading}
          />
        </Box>
      </FormProvider>
    );
  },
);
